import { View, Text } from 'react-native';
import { Star } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';

interface StarRatingProps {
  rating: number; // 0-5
  maxStars?: number;
  size?: 'sm' | 'md' | 'lg';
  showValue?: boolean;
  showCount?: boolean;
  count?: number;
}

const SIZE_CONFIG = {
  sm: {
    starSize: 12,
    gap: 'gap-0.5',
    textSize: 'text-xs',
  },
  md: {
    starSize: 16,
    gap: 'gap-1',
    textSize: 'text-sm',
  },
  lg: {
    starSize: 20,
    gap: 'gap-1',
    textSize: 'text-base',
  },
};

export default function StarRating({
  rating,
  maxStars = 5,
  size = 'md',
  showValue = false,
  showCount = false,
  count = 0,
}: StarRatingProps) {
  const colors = useThemeColors();
  const config = SIZE_CONFIG[size];
  const clampedRating = Math.min(Math.max(0, rating), maxStars);

  // Generate stars
  const stars = [];
  for (let i = 1; i <= maxStars; i++) {
    const fillAmount = Math.min(Math.max(clampedRating - (i - 1), 0), 1);
    const isFull = fillAmount >= 0.75;
    const isHalf = fillAmount >= 0.25 && fillAmount < 0.75;
    const isEmpty = fillAmount < 0.25;

    stars.push(
      <View key={i} className="relative">
        <Star
          size={config.starSize}
          color={isEmpty ? colors.border.medium : colors.amber.main}
          fill={isFull ? colors.amber.main : 'transparent'}
          strokeWidth={isFull ? 0 : 1.5}
        />
        {isHalf && (
          <View
            className="absolute top-0 left-0 overflow-hidden"
            style={{ width: config.starSize / 2, height: config.starSize }}
          >
            <Star
              size={config.starSize}
              color={colors.amber.main}
              fill={colors.amber.main}
              strokeWidth={0}
            />
          </View>
        )}
      </View>
    );
  }

  return (
    <View className={`flex-row items-center ${config.gap}`}>
      <View className={`flex-row items-center ${config.gap}`}>
        {stars}
      </View>
      {showValue && (
        <Text
          style={{ color: colors.text.primary }}
          className={`${config.textSize} font-semibold ml-1`}
        >
          {clampedRating.toFixed(1)}
        </Text>
      )}
      {showCount && count > 0 && (
        <Text
          style={{ color: colors.text.tertiary }}
          className={`${config.textSize} ml-1`}
        >
          ({count})
        </Text>
      )}
    </View>
  );
}

// Compact rating display (4.8/5 format)
export function CompactRating({
  rating,
  count,
  size = 'md',
}: {
  rating: number;
  count?: number;
  size?: 'sm' | 'md' | 'lg';
}) {
  const colors = useThemeColors();
  const config = SIZE_CONFIG[size];

  return (
    <View className={`flex-row items-center ${config.gap}`}>
      <Star
        size={config.starSize}
        color={colors.amber.main}
        fill={colors.amber.main}
        strokeWidth={0}
      />
      <Text
        style={{ color: colors.text.primary }}
        className={`${config.textSize} font-semibold`}
      >
        {rating.toFixed(1)}
      </Text>
      {count !== undefined && (
        <Text
          style={{ color: colors.text.tertiary }}
          className={`${config.textSize}`}
        >
          ({count} reviews)
        </Text>
      )}
    </View>
  );
}
